import { candidatePhotoType } from '@/enums';
import BaseModel from '../BaseModel';
import Interview from '../Interview/Interview';
import OfficeInterview from '../OfficeInterview/OfficeInterview';
import CandidateLog from './CandidateLog';
import PublicToken from './PublicToken';
import Resume from './Resume';
import CandidatePhoto from './CandidatePhoto';
import CandidateTestScore from './CandidateTestScore';

export default class Candidate extends BaseModel {
  static entity = 'candidates';

  static primaryKey = 'id';

  static fields() {
    return {
      id: this.attr(null),
      hashId: this.attr(null),
      vacancyId: this.attr(null),
      firstName: this.string(null).nullable(),
      lastName: this.string(null).nullable(),
      phone: this.string(null).nullable(),
      email: this.string(null).nullable(),
      invitationType: this.string(null).nullable(),
      rating: this.number(null).nullable(),
      birthDate: this.string(null).nullable(),
      location: this.string(null).nullable(),
      status: this.string(null).nullable(),
      state: this.string(null).nullable(),
      hhNegotiationData: this.attr(null).nullable(),
      hhIntegration: this.attr(null).nullable(),
      inviteLink: this.string(null).nullable(),
      isNew: this.boolean(false).nullable(),
      selfRefused: this.boolean(false).nullable(),
      photos: this.hasMany(CandidatePhoto, 'candidateId'),
      resume: this.hasOne(Resume, 'candidateId'),
      publicToken: this.hasOne(PublicToken, 'candidateId'),
      vacancy: this.belongsTo('vacancies', 'vacancyId'),
      interview: this.hasOne(Interview, 'candidateId'),
      officeInterview: this.hasOne(OfficeInterview, 'candidateId'),
      officeInterviews: this.hasMany(OfficeInterview, 'candidateId'),
      logs: this.hasMany(CandidateLog, 'candidateId'),
      permissions: this.attr({
        notifiable: false,
      }),
      photoUrl: this.string(null).nullable(),
      createdAt: this.attr(null),
      interviewedAt: this.attr(null).nullable(),
      canCreateOfficeInterview: this.boolean(false),
      canCreateOnlineInterview: this.boolean(false),
      canCreateVideoCall: this.boolean(false),
      canCreateUploadedVideo: this.boolean(true),
      quotaWrittenOff: this.attr(null).nullable(),
      testFinishedAt: this.string(null).nullable(),
      testScore: this.hasOne(CandidateTestScore, 'candidateId'),
      testStartedAt: this.string(null).nullable(),
      additionalFields: this.attr(null).nullable(),
      aiRating: this.attr(null).nullable(),
    };
  }

  static state() {
    return {
      ...super.state(),
      total: 0,
      perPage: 40,
      currentPage: 0,
      lastPage: 0,
    };
  }

  static async index(params) {
    const request = await this.api().fetchAll(params);

    const { data, meta } = request.response.data;

    const entities = await this.insert({ data });

    await this.commit((state) => {
      state.total = meta.total;
      state.perPage = meta.per_page;
      state.currentPage = meta.current_page;
      state.lastPage = meta.last_page;
    });

    return data;
  }

  static getMetaData() {
    const {
      total, perPage, currentPage, lastPage,
    } = this.store().state.entities[this.entity];
    return {
      total, perPage, currentPage, lastPage,
    };
  }

  static apiConfig = {
    actions: {
      fetchAllByCompanyId(id, filter, sorting, photoType, key = null) {
        return this.model.handleFetching(() => this.get(
          `companies/${id}/candidates?filters=${JSON.stringify(filter)}&photoType=${photoType}&sorting=${JSON.stringify(sorting)}`,
        ), key || `all_by_company_${id}`);
      },
      fetchAllByVacancyId(id, photoType = candidatePhotoType.S50, key = null) {
        return this.model.handleFetching(
          () => this.get(`/vacancies/${id}/candidates?photoType=${photoType}`),
          key || `all_by_vacancy_${id}`,
        );
      },
      async fetchPaginatedCandidates(id, payload) {
        const params = { ...payload };
        if (payload.search) {
          params.search = payload.search;
        }
        const response = await this.get(`vacancies/${id}/paginated-candidates`, {
          params,
        });

        if (response?.response?.data) {
          const responseData = response.response.data;
          let dataToInsert = null;

          if (responseData.data && Array.isArray(responseData.data)) {
            dataToInsert = responseData.data;
          } else if (Array.isArray(responseData)) {
            dataToInsert = responseData;
          }

          if (dataToInsert && dataToInsert.length > 0) {
            if (payload.search && payload.page === 1) {
              const candidatesToDelete = this.model.query().where('vacancyId', id).get();
              candidatesToDelete.forEach((candidate) => {
                this.model.delete(candidate.id);
              });
            }
            await this.model.insertOrUpdate({ data: dataToInsert });
          } else if (payload.search && payload.page === 1) {
            const candidatesToDelete = this.model.query().where('vacancyId', id).get();
            candidatesToDelete.forEach((candidate) => {
              this.model.delete(candidate.id);
            });
          }
        }
        return response;
      },
      fetchById(id, key = null) {
        return this.model.handleFetching(() => this.get(`candidates/${id}`), key || id);
      },
      store(vacancyId, payload) {
        return this.post(`vacancies/${vacancyId}/candidate`, payload);
      },
      storeWithoutInvitation(vacancyId, payload) {
        return this.post(`vacancies/${vacancyId}/candidate/without_invitation`, payload);
      },
      update(id, payload) {
        return this.patch(`candidates/${id}`, payload);
      },
      updateWithoutSave(id, payload) {
        return this.patch(`candidates/${id}`, payload, { save: false });
      },
      async updateStatus(id, status) {
        const oldStatus = this.model.find(id).status;

        this.model.update({ where: id, data: { status } });

        try {
          return this.patch(`candidates/${id}/status`, { status }, { save: false });
        } catch (e) {
          this.model.update({ where: id, data: { status: oldStatus } });
          throw e;
        }
      },
      async updateStatusMass(payload) {
        return this.patch('candidates/status/mass-update', payload, { save: false });
      },
      async updateRating(id, rating) {
        return this.patch(`candidates/${id}/rating`, { rating }, { save: false });
      },
      notify(id, payload) {
        return this.post(`candidates/${id}/contact`, payload, { save: false });
      },
      fetchCommunication(code) {
        return this.get(`candidates/communication/${code}`, { save: false });
      },
      fetchCommunicationMessagesByCandidate(candidateId, params = { page: 1, perPage: 50 }) {
        return this.get(`candidates/communication/messages/${candidateId}`, {
          params,
          save: false,
        });
      },
      sendCommunicationMessageByCandidate(candidateId, payload) {
        return this.post(`candidates/communication/${candidateId}`, payload, { save: false });
      },
      markCommunicationAsRead(candidateId) {
        return this.post(`candidates/communication/mark-as-read/${candidateId}`, {}, { save: false });
      },
      async notifyMass(payload) {
        return this.post('candidates/mass-contact', payload, { save: false });
      },
      destroy(id) {
        return this.delete(`candidates/${id}`);
      },
      async importCandidates(vacancyId, payload) {
        return this.post(`vacancies/${vacancyId}/import`, payload, { save: false });
      },
      async importCandidatesWithoutInvitation(vacancyId, payload) {
        return this.post(`vacancies/${vacancyId}/import/without_invitation`, payload, { save: false });
      },
      async inviteToOnlineInterview(id) {
        return this.post(`candidates/${id}/invite_to_virtual_interview`);
      },
      async exportCandidates(payload) {
        return this.post('candidates/export/xlsx', payload, { responseType: 'arraybuffer' });
      },
      async open(candidateId) {
        return this.post('accounting/open_candidate', {
          candidateId,
        }, {
          save: false,
        });
      },
      fetchAll(params) {
        return this.post('candidates/get/by_company', {
          ...params,
        });
      },
    },
  }
}
